import React, { useState } from "react";
import { auth } from "../firebase/config";
import styled from "styled-components";
import { FaCheckCircle, FaStar, FaCrown, FaTimes } from "react-icons/fa";
import { FiArrowRight } from "react-icons/fi";
import bgD from "../assets/loginBg2.png";
import bgL from "../assets/loginBg.png";
import { useTranslation } from "react-i18next";

const UpgradePage = ({ theme }) => {
  const { t } = useTranslation();
  const [isYearly, setIsYearly] = useState(false);

  // Different payment links for monthly vs yearly
  const upgradeMonthly = () => {
    const uid = auth.currentUser.uid;
    window.location.href = `https://buy.stripe.com/3cI00k1jV6gCgSk7jm0Ba01?client_reference_id=${uid}`;
  };

  const upgradeYearly = () => {
    const uid = auth.currentUser.uid;
    // Assuming you have a different Stripe link for yearly payments
    window.location.href = `https://buy.stripe.com/9B600k2nZdJ4dG847a0Ba02?client_reference_id=${uid}`;
  };

  const packages = [
    {
      name: t("upgrade.packages.starter.name"),
      price: t("upgrade.packages.starter.price"),
      yearlyPrice: t("upgrade.packages.starter.price"),
      period: isYearly
        ? t("upgrade.packages.starter.period")
        : t("upgrade.packages.starter.period"),
      description: t("upgrade.packages.starter.description"),
      features: [
        {
          text: t("upgrade.packages.starter.features.taskManager"),
          included: true,
        },
        {
          text: t("upgrade.packages.starter.features.habitTracker"),
          included: true,
        },
        {
          text: t("upgrade.packages.starter.features.reminders"),
          included: true,
        },
        {
          text: t("upgrade.packages.starter.features.workHours"),
          included: true,
        },
        {
          text: t("upgrade.packages.starter.features.financesBasic"),
          included: true,
        },
        {
          text: t("upgrade.packages.starter.features.habitStreaks"),
          included: true,
        },
        {
          text: t("upgrade.packages.starter.features.advancedAnalytics"),
          included: false,
        },
        {
          text: t("upgrade.packages.starter.features.premiumTemplates"),
          included: false,
        },
        {
          text: t("upgrade.packages.starter.features.prioritySupport"),
          included: false,
        },
        {
          text: t("upgrade.packages.starter.features.customReports"),
          included: false,
        },
        {
          text: t("upgrade.packages.starter.features.teamCollaboration"),
          included: false,
        },
        {
          text: t("upgrade.packages.starter.features.apiAccess"),
          included: false,
        },
      ],
      color: "#9281dd",
      icon: <FaCheckCircle />,
      buttonText: t("upgrade.packages.starter.buttonText"),
      disabled: true,
    },
    {
      name: t("upgrade.packages.pro.name"),
      price: t("upgrade.packages.pro.price"),
      yearlyPrice: t("upgrade.packages.pro.yearlyPrice"),
      originalPrice: t("upgrade.packages.pro.originalPrice"),
      yearlyOriginalPrice: t("upgrade.packages.pro.yearlyOriginalPrice"),
      period: isYearly
        ? t("upgrade.packages.pro.periodYearly")
        : t("upgrade.packages.pro.periodMonthly"),
      description: t("upgrade.packages.pro.description"),
      features: [
        {
          text: t("upgrade.packages.pro.features.advancedTaskManager"),
          included: true,
        },
        {
          text: t("upgrade.packages.pro.features.advancedHabitTracker"),
          included: true,
        },
        {
          text: t("upgrade.packages.pro.features.financesAdvanced"),
          included: true,
        },
        { text: t("upgrade.packages.pro.features.myProgress"), included: true },
        {
          text: t("upgrade.packages.pro.features.customQuotes"),
          included: true,
        },
        {
          text: t("upgrade.packages.pro.features.streaksInsights"),
          included: true,
        },
        {
          text: t("upgrade.packages.pro.features.goalTrackers"),
          included: true,
        },
        { text: t("upgrade.packages.pro.features.workHours"), included: true },
        { text: t("upgrade.packages.pro.features.brainDump"), included: true },
        {
          text: t("upgrade.packages.pro.features.customCurrency"),
          included: true,
        },
        {
          text: t("upgrade.packages.pro.features.advancedAnalytics"),
          included: true,
        },
        { text: t("upgrade.packages.pro.features.aiInsights"), included: true },
      ],
      color: "#9281dd",
      icon: <FaStar />,
      buttonText: t("upgrade.packages.pro.buttonText"),
      highlighted: true,
      action: isYearly ? upgradeYearly : upgradeMonthly,
    },
    {
      name: t("upgrade.packages.enterprise.name"),
      price: t("upgrade.packages.enterprise.price"),
      yearlyPrice: t("upgrade.packages.enterprise.price"),
      period: t("upgrade.packages.enterprise.period"),
      description: t("upgrade.packages.enterprise.description"),
      features: [
        {
          text: t("upgrade.packages.enterprise.features.everythingInPro"),
          included: true,
        },
        {
          text: t("upgrade.packages.enterprise.features.unlimitedTeam"),
          included: true,
        },
        {
          text: t("upgrade.packages.enterprise.features.whiteLabel"),
          included: true,
        },
        {
          text: t("upgrade.packages.enterprise.features.dedicatedSupport"),
          included: true,
        },
        {
          text: t("upgrade.packages.enterprise.features.customIntegrations"),
          included: true,
        },
        {
          text: t("upgrade.packages.enterprise.features.slaGuarantee"),
          included: true,
        },
        {
          text: t("upgrade.packages.enterprise.features.onPremise"),
          included: true,
        },
        {
          text: t("upgrade.packages.enterprise.features.training"),
          included: true,
        },
        {
          text: t("upgrade.packages.enterprise.features.advancedSecurity"),
          included: true,
        },
        {
          text: t("upgrade.packages.enterprise.features.workflowAutomation"),
          included: true,
        },
      ],
      color: "#10b981",
      icon: <FaCrown />,
      buttonText: t("upgrade.packages.enterprise.buttonText"),
      action: () => window.open("mailto:sales@example.com"),
    },
  ];

  return (
    <Container theme={theme}>
      <BackgroundOverlay theme={theme} blurAmount="15px" />
      <Header>
        <h3 className="topTitle">{t("upgrade.header.topTitle")}</h3>
        <h1 className="title">
          {t("upgrade.header.title.part1")}{" "}
          <span>{t("upgrade.header.title.span1")}</span>{" "}
          {t("upgrade.header.title.part2")}{" "}
          <span>{t("upgrade.header.title.span2")}</span>
        </h1>
        {/* <p className="subtitle">{t("upgrade.header.subtitle")}</p> */}

        {/* Billing Toggle */}
        <BillingToggle>
          <ToggleLabel>{t("upgrade.billing.monthly")}</ToggleLabel>
          <ToggleSwitch>
            <input
              type="checkbox"
              checked={isYearly}
              onChange={() => setIsYearly(!isYearly)}
              id="billing-toggle"
            />
            <ToggleSlider />
          </ToggleSwitch>
          <ToggleLabel active={isYearly}>
            {t("upgrade.billing.yearly")}{" "}
            <DiscountBadge>{t("upgrade.billing.save20")}</DiscountBadge>
          </ToggleLabel>
        </BillingToggle>
      </Header>

      <CardsGrid>
        {packages.map((plan, index) => (
          <Card key={index} $highlighted={plan.highlighted}>
            {plan.highlighted && (
              <PopularBadge>
                <FaStar size={12} /> {t("upgrade.badge.mostPopular")}
              </PopularBadge>
            )}

            <PlanHeader>
              <PlanIcon $color={plan.color}>{plan.icon}</PlanIcon>
              <PlanName>{plan.name}</PlanName>
              <PlanDescription>{plan.description}</PlanDescription>
            </PlanHeader>

            <PlanPrice>
              {isYearly && plan.yearlyOriginalPrice && (
                <OriginalPrice>{plan.yearlyOriginalPrice}</OriginalPrice>
              )}
              {!isYearly && plan.originalPrice && (
                <OriginalPrice>{plan.originalPrice}</OriginalPrice>
              )}
              <Price>
                {isYearly ? plan.yearlyPrice || plan.price : plan.price}
              </Price>
              <Period>{plan.period}</Period>
              {isYearly && plan.highlighted && (
                <EffectivePrice>{t("upgrade.effectivePrice")}</EffectivePrice>
              )}
            </PlanPrice>

            <FeaturesList>
              {plan.features.map((feature, idx) => (
                <FeatureItem key={idx} $included={feature.included}>
                  {feature.included ? (
                    <FaCheckCircle color={plan.color} size={14} />
                  ) : (
                    <FaTimes color="#ef4444" size={14} />
                  )}
                  <span>{feature.text}</span>
                </FeatureItem>
              ))}
            </FeaturesList>

            <ActionButton
              $color={plan.color}
              $highlighted={plan.highlighted}
              onClick={plan.action}
              disabled={plan.disabled}
            >
              {plan.buttonText}
              {!plan.disabled && <FiArrowRight size={18} />}
            </ActionButton>
          </Card>
        ))}
      </CardsGrid>
    </Container>
  );
};

// New styled components for toggle and features
const BillingToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin: 2rem 0 1rem;
`;

const ToggleLabel = styled.span`
  font-size: 0.95rem;
  color: ${(props) => (props.active ? "#8b5cf6" : "var(--fontSecondaryColor)")};
  font-weight: ${(props) => (props.active ? "600" : "400")};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 60px;
  height: 30px;

  input {
    opacity: 0;
    width: 0;
    height: 0;

    &:checked + span {
      background-color: #8b5cf6;
    }

    &:checked + span:before {
      transform: translateX(30px);
    }
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #e2e8f0;
  transition: 0.4s;
  border-radius: 34px;

  &:before {
    position: absolute;
    content: "";
    height: 22px;
    width: 22px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: 0.4s;
    border-radius: 50%;
  }
`;

const DiscountBadge = styled.span`
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const YearlyDiscount = styled.div`
  background: linear-gradient(135deg, #8b5cf6, #6366f1);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  display: inline-block;
  margin-bottom: 0.5rem;
`;

const EffectivePrice = styled.div`
  font-size: 0.875rem;
  color: #10b981;
  font-weight: 500;
  margin-top: 0.25rem;
`;

const OriginalPrice = styled.div`
  font-size: 1.5rem;
  font-weight: 400;
  color: var(--fontSecondaryColor);
  text-decoration: line-through;
  margin-bottom: 0.25rem;
  opacity: 0.7;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  color: ${(props) =>
    props.$included ? "var(--fontSecondaryColor)" : "#94a3b8"};
  font-size: 0.95rem;

  span {
    flex: 1;
    text-decoration: ${(props) => (props.$included ? "none" : "line-through")};
    opacity: ${(props) => (props.$included ? "1" : "0.7")};
  }
`;

const Container = styled.div`
  min-height: 100vh;
  padding: 4rem 2rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  background: url(${(props) => (props.theme === "dark" ? bgD : bgL)});
  transition: 0.5s;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  -webkit-backdrop-filter: blur(1px);
  backdrop-filter: blur(1px);
  @media (max-width: 650px) {
    padding: 2rem 0rem;
  }
  @media (max-width: 750px) {
    padding-top: 4em;
  }
`;

const BackgroundOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url(${(props) => (props.theme === "dark" ? bgD : bgL)});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  filter: blur(${(props) => props.blurAmount || "2px"});
  z-index: -1;

  /* Add an overlay for better readability */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${(props) =>
      props.theme === "dark"
        ? "rgba(12, 13, 31, 0.342)"
        : "rgba(255, 255, 255, 0.61)"};
  }
`;

const Header = styled.div`
  .topTitle {
    text-align: center;
    color: #9281dd;
    font-weight: 200;
    border: 1px solid #463d6e;
    display: flex;
    width: auto;
    justify-content: center;
    align-items: center;
    width: 480px;
    margin: auto;
    padding: 7px 5px;
    border-radius: 20px;
    text-transform: uppercase;
  }
  .title {
    font-weight: 400;
    font-size: 2vw;
    text-align: center;
    line-height: 1em;
    margin-top: 40px;
    color: var(--fontPrimaryColor);
    font-family: "Montserrat", sans-serif;
    span {
      color: #9281dd;
      font-weight: 400;
    }
  }
  .subtitle {
    margin-top: 10px;
    text-align: center;
    color: var(--fontSecondaryColor);
  }
  @media (max-width: 1000px) {
    .title {
      font-size: 5vw;
    }
  }
  @media (max-width: 650px) {
    .title {
      padding: 0 1em;
      font-size: 9vw;
    }
    .topTitle {
      width: 95%;
    }
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1500px;
  width: 100%;
  margin-top: 3em;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 0 1rem;
  }
`;

const Card = styled.div`
  background: var(--lowPopupBg2);
  border-radius: 24px;
  -webkit-backdrop-filter: blur(15px);
  backdrop-filter: blur(15px);
  padding: 2rem;
  position: relative;
  box-shadow: 0 4px 20px var(--shadowColor);
  border: 1px solid var(--shadowColor);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;

  ${(props) =>
    props.$highlighted &&
    `
    border: 2px solid #8b5cf6;
    box-shadow: 0 8px 40px var(--shadowColor);
    transform: translateY(-4px);
  `}

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
  }
  @media (max-width: 650px) {
    width: 100%;
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #8b5cf6, #6366f1);
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PlanHeader = styled.div`
  margin-bottom: 10px;
`;

const PlanIcon = styled.div`
  width: 48px;
  height: 48px;
  background: ${(props) => props.$color}15;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.$color};
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const PlanName = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--fontPrimaryColor);
  margin: 0 0 0.2rem 0;
`;

const PlanDescription = styled.p`
  color: var(--fontSecondaryColor);
  font-size: 0.95rem;
  margin: 0;
`;

const PlanPrice = styled.div`
  padding: 1rem 0;
  border-bottom: 1px solid #f1f5f9;
`;

const Price = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: var(--fontPrimaryColor);
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Period = styled.div`
  color: var(--fontSecondaryColor);
  font-size: 0.95rem;
`;

const FeaturesList = styled.div`
  flex: 1;
  margin-bottom: 2rem;
  margin-top: 1rem;
`;

const ActionButton = styled.button`
  width: 100%;
  padding: 1rem 1.5rem;
  background: ${(props) =>
    props.$highlighted
      ? `linear-gradient(135deg, ${props.$color}, ${props.$color}cc)`
      : props.$color
      ? `${props.$color}15`
      : "#f1f5f9"};
  color: ${(props) =>
    props.$highlighted ? "white" : props.$color || "#64748b"};
  border: ${(props) =>
    props.$highlighted ? "none" : `1px solid ${props.$color}30`};
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  transition: all 0.3s ease;
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px
      ${(props) => (props.$color ? `${props.$color}30` : "rgba(0,0,0,0.1)")};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

export default UpgradePage;
